import { useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { DocumentTypeEnumType } from "@libs/api/patients";
import { FilesApi } from "@/data/api/filesApi";
import {
  useAddDocumentMutation,
  useGeneratePresignedUrlForUploadMutation,
} from "@/data/mutations/uploadedDocumentsMutations";
import { UploadedDocumentQueryKeys } from "@/data/queries/uploadedDocumentsQueries";
import { SelectedFileType } from "@/hooks/useFilePicker";
import {
  cleanupDocumentUploadStore,
  startPollingForDocument,
  startUpload,
  updateUploadStatus,
  useIsUploading,
  usePendingUploads,
} from "@/stores/documentUploadStore";
import { dateAsDayjs } from "@/utils/date";
import { logger } from "@/utils/logger";

type UseUploadDocumentParamsType = {
  householdMemberId: string;
};

type UploadDocumentsParamsType = {
  files: SelectedFileType[];
  documentCategoryId: string;
  documentType: DocumentTypeEnumType;
  // Runs once the document exists, before malware scanning finishes, so the
  // caller can attach it to whatever it was uploaded for.
  onDocumentAdded?: (documentId: string) => Promise<unknown>;
};

export const useUploadDocument = ({
  householdMemberId,
}: UseUploadDocumentParamsType) => {
  const pendingUploads = usePendingUploads();
  const isUploading = useIsUploading();

  const queryClient = useQueryClient();
  const generatePresignedUrlMutation =
    useGeneratePresignedUrlForUploadMutation();
  const addDocumentMutation = useAddDocumentMutation();

  // On unmount: stop polling and reset store state
  // The upload flow (presigned URL → storage → addDocument) continues as it's a Promise
  // React Query will refetch the documents list on remount
  useEffect(() => {
    return () => {
      cleanupDocumentUploadStore();
    };
  }, []);

  const uploadDocuments = useCallback(
    async ({
      files,
      documentCategoryId,
      documentType,
      onDocumentAdded,
    }: UploadDocumentsParamsType) => {
      if (files.length === 0) {
        return { isSuccess: false, error: "No files selected" };
      }

      // Generate a unique upload ID
      const uploadId = `upload_${dateAsDayjs().unix()}`;

      // Add to store with UPLOADING status
      startUpload({
        uploadId,
        documentType,
        documentCategoryId,
        householdMemberId,
        files,
      });

      try {
        // Step 1: Generate presigned URLs
        const presignedResponse =
          await generatePresignedUrlMutation.mutateAsync({
            householdMemberId,
            documentCategoryId,
            files: files.map(f => ({
              fileName: f.fileName,
              contentType: f.mimeType,
            })),
          });

        if (!presignedResponse) {
          updateUploadStatus({ uploadId, status: "FAILED" });
          return {
            isSuccess: false,
            error: "Failed to generate presigned URLs",
          };
        }

        const { files: presignedFiles } = presignedResponse;

        // Step 2: Upload files to storage
        const uploadResults = await Promise.all(
          files.map((file, index) =>
            FilesApi.uploadFile({
              presignedUrl: presignedFiles[index]!.url,
              file,
            }),
          ),
        );

        const isAllUploadsSuccessful = uploadResults.every(Boolean);

        if (!isAllUploadsSuccessful) {
          updateUploadStatus({ uploadId, status: "FAILED" });
          return { isSuccess: false, error: "Failed to upload files" };
        }

        // Step 3: Call addDocument mutation with object keys
        const objectKeys = presignedFiles.map(f => f.objectKey);
        const document = await addDocumentMutation.mutateAsync({ objectKeys });

        if (!document) {
          updateUploadStatus({ uploadId, status: "FAILED" });
          return { isSuccess: false, error: "Failed to add document" };
        }

        await onDocumentAdded?.(document.id);

        // Step 4: Update status to SCANNING and start polling
        updateUploadStatus({
          uploadId,
          status: "SCANNING",
          documentId: document.id,
        });

        startPollingForDocument({
          uploadId,
          documentId: document.id,
          onComplete: () => {
            void queryClient.invalidateQueries({
              queryKey: UploadedDocumentQueryKeys.list({
                householdMemberId,
              }),
            });
            // Section completion is derived server-side — refresh it too.
            void queryClient.invalidateQueries({
              queryKey: UploadedDocumentQueryKeys.requirements(),
            });
          },
        });
      } catch (error) {
        logger.error({ error, message: "Document upload failed" });
        updateUploadStatus({ uploadId, status: "FAILED" });
      }
    },
    [
      householdMemberId,
      generatePresignedUrlMutation,
      addDocumentMutation,
      queryClient,
    ],
  );

  return {
    pendingUploads,
    uploadDocuments,
    isUploading,
  };
};
